import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface QuestionCountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCount: (count: number) => void;
  subjectName: string;
  totalQuestionsAvailable: number;
}

export const QuestionCountModal: React.FC<QuestionCountModalProps> = ({
  isOpen,
  onClose,
  onSelectCount,
  subjectName,
  totalQuestionsAvailable
}) => {
  console.log('🎯 [DEBUG] QuestionCountModal renderizzato:', { isOpen, subjectName, totalQuestionsAvailable });
  
  if (!isOpen) return null;

  // Funzione per ottenere lo stile CSS dinamico con alpha INVERTITO
  const getGreenStyleWithAlpha = (questionCount: number, maxQuestions: number) => {
    const ratio = questionCount / maxQuestions;
    // INVERTITO: Alpha va da 0.2 (poco visibile) a 0.8 (molto visibile)
    const alpha = Math.max(0.2, 0.2 + (ratio * 0.6));
    
    return {
      background: `linear-gradient(135deg, rgba(34, 197, 94, ${alpha}) 0%, rgba(22, 163, 74, ${alpha * 0.8}) 100%)`,
    };
  };

  // Funzione per ottenere lo stile overlay con alpha dinamico INVERTITO
  const getOverlayStyleWithAlpha = (questionCount: number, maxQuestions: number) => {
    const ratio = questionCount / maxQuestions;
    // INVERTITO: Alpha va da 0.1 (poco visibile) a 0.4 (molto visibile)
    const alpha = Math.max(0.1, 0.1 + (ratio * 0.3));
    
    return {
      background: `linear-gradient(135deg, rgba(22, 163, 74, ${alpha}) 0%, rgba(21, 128, 61, ${alpha * 0.8}) 100%)`,
    };
  };

  // Crea le opzioni basate sul numero effettivo di domande disponibili
  const createQuestionCountOptions = () => {
    const options = [];
    
    // Opzioni standard solo se disponibili
    if (totalQuestionsAvailable >= 10) {
      options.push({ count: 10, label: '10 domande', description: 'Quiz rapido' });
    }
    if (totalQuestionsAvailable >= 20) {
      options.push({ count: 20, label: '20 domande', description: 'Quiz medio' });
    }
    if (totalQuestionsAvailable >= 30) {
      options.push({ count: 30, label: '30 domande', description: 'Quiz completo' });
    }
    
    // Aggiungi "Tutte" solo se ci sono più di 30 domande o se è diverso dalle opzioni standard
    if (totalQuestionsAvailable > 30 || (totalQuestionsAvailable !== 10 && totalQuestionsAvailable !== 20 && totalQuestionsAvailable !== 30)) {
      options.push({ 
        count: totalQuestionsAvailable, 
        label: `Tutte (${totalQuestionsAvailable})`, 
        description: 'Quiz completo' 
      });
    }
    
    return options;
  };

  const questionCountOptions = createQuestionCountOptions();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full max-h-[70vh] md:max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Numero di domande
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {subjectName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-gray-700 mb-4 text-sm">
            Quante domande vuoi affrontare?
          </p>

          <div className="space-y-2">
            {questionCountOptions.map((option) => (
              <div
                key={option.count}
                className="group relative overflow-hidden rounded-lg shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
                style={getGreenStyleWithAlpha(option.count, totalQuestionsAvailable)}
                onClick={() => {
                  console.log('🎯 [DEBUG] Click su opzione:', option.count, 'domande');
                  onSelectCount(option.count);
                  onClose();
                }}
              >
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={getOverlayStyleWithAlpha(option.count, totalQuestionsAvailable)}
                ></div>
                
                {/* Contenuto principale */}
                <div className="relative p-3 h-full flex flex-col bg-white/10 backdrop-blur-sm rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-800 group-hover:text-white transition-colors duration-300 text-sm">
                        {option.label}
                      </div>
                      <div className="text-xs text-gray-700 group-hover:text-green-100 transition-colors duration-300">
                        {option.description}
                      </div>
                    </div>
                    <div className="text-green-600 group-hover:text-white transition-colors duration-300">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Info - Compatta */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-4 w-4 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-2">
                <p className="text-xs text-blue-800">
                  <strong>Suggerimento:</strong> Inizia con 10 domande per familiarizzare.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
};
